import { Component, OnInit, OnDestroy, AfterViewInit, signal, computed, inject, effect } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, timer } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import { EmployeeInfoService } from '@core/services/employee-info.service';
import { PortalSettingsService } from '@core/services/portal-settings.service';
import { EventService } from '@core/services/event.service';
import { UtilityService } from '@core/services/utility.service';
import { PortalModulesService } from '@core/services/portal-modules.service';
import { ClockInOutService } from '@core/services/clock-in-out.service';

import { HomeService } from '@pages/home/services/home.service';
import { HomeApiService } from '@pages/home/services/home.api.service';

import { ClockinJobModalComponent } from '@modals/home/clockin-job-modal/clockin-job-modal.component';
import { ErrorModalComponent } from '@modals/shared/error-modal/error-modal.component';
import { ClockoutModalComponent } from '@modals/home/clockout-modal/clockout-modal.component';
import { PaperlessModalComponent } from '@modals/home/paperless-modal/paperless-modal.component';
import { PasswordWarningModalComponent } from '@modals/home/password-warning-modal/password-warning-modal.component';
import { WarningModalComponent } from '@modals/shared/warning-modal/warning-modal.component';
import { AlertModalComponent } from '@modals/shared/alert-modal/alert-modal.component';

import { PortalModule } from '@typeDefs/home/responses/home';

import { OrderByPipe } from '@shared/pipe/order-by.pipe';

import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SimpleLookupResult } from '../../../type-definitions/general/general';
import { ClockAction_Request } from '@typeDefs/employee/requests/employee';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.css'],
})
export class NavigationComponent implements OnInit, OnDestroy, AfterViewInit {
  // injected services
  private router = inject(Router);
  private homeService = inject(HomeService);
  private homeApiService = inject(HomeApiService);
  private modalService = inject(BsModalService);
  private ngbModalService = inject(NgbModal);
  private eventService = inject(EventService);
  private utilityService = inject(UtilityService);
  private clockinoutService = inject(ClockInOutService);
  private document = inject(DOCUMENT);
  private window = this.document.defaultView;
  private employeeInfo = inject(EmployeeInfoService).getEmployeeInfo();
  private portalModules = inject(PortalModulesService).getPortalModules();
  private portalSettings$ = inject(PortalSettingsService).portalSettings$;

  // readonly fields
  readonly employeeName = `${this.employeeInfo.FirstName != null && this.employeeInfo.FirstName != '' ? this.employeeInfo.FirstName : ''} ${this.employeeInfo.LastName}`;
  readonly prco = this.employeeInfo.PRCo;
  readonly employee = this.employeeInfo.Employee;
  readonly viewpointOneUserID = this.employeeInfo.ViewpointOneUserID;
  readonly trimbleID = this.employeeInfo.TrimbleID;
  readonly loginType = this.employeeInfo.LoginType;
  readonly vpUserName = this.employeeInfo.VPUserName;
  readonly expenseReport = '/employee/expensereport';
  readonly userAccess = '/Admin/ManageEmployees';
  readonly mobileInitUrl = `${this.employeeInfo.IsAuthFMMobile ? `kdstimecard://${this.window.location.origin}` : null}`;
  readonly currencySymbol = this.utilityService.getCurrencySymbol(this.employeeInfo.PRCo);
  readonly viewpointOneUrl =
    this.utilityService.getBrowserLanguage() === 'en-AU' ? 'https://au.team.viewpoint.com' : 'https://team.viewpoint.com';
  readonly isModuleAdmin =
    this.employeeInfo.IsBulletinAdmin ||
    this.employeeInfo.IsChecklistAdmin ||
    this.employeeInfo.IsHuddleAdmin ||
    this.employeeInfo.IsOnboardAdmin ||
    this.employeeInfo.IsPurchasingAdmin ||
    this.employeeInfo.IsSecurityAdmin ||
    this.employeeInfo.IsTimecardAdmin ||
    this.employeeInfo.IsTrainingAdmin ||
    this.employeeInfo.IsTrainingClassAdmin ||
    this.employeeInfo.IsVacationAdmin ||
    this.employeeInfo.IsVacationDashAdmin;
  readonly suggestionUrl = `${this.employeeInfo.IsSystemAdmin || this.isModuleAdmin ? 'https://keystyle.ideas.aha.io/ideas' : null}`;

  // signals
  userContext = this.homeApiService.userContext;
  pRCO = computed(() => this.userContext()?.PRCo);
  loginMode = computed(() => this.userContext()?.LoginMode);
  showQuickAdd = signal(this.employeeInfo.IsAuthExpense);
  isOver = computed(() => this.userContext()?.IsOver);
  portalName = computed(() => this.userContext()?.PortalName);
  logoUrlBroken = signal<boolean>(false);
  logoUrl = computed(() => {
    if (this.logoUrlBroken()) {
      return '/Cache/images/viewpoint-logo2.png';
    }
    if (!this.pRCO()) {
      return null;
    }
    return `/Company/GetLogo?co=${this.pRCO()}&size=S`;
  });

  canMealBreakClockIn = computed(() => {
    return this.checkCanMealBreakClockIn();
  })

  portalSettings = toSignal(this.portalSettings$);
  tidEnabled = computed(() => this.portalSettings().TidEnabled.ConfigValue === '1');
  showTeamLinkOnNav = computed(() => this.portalSettings().ShowTeamLinkOnNav.ConfigValue == '1');
  navBarClockInEnabled = computed(() => this.portalSettings().NavBarClockInEnabled.ConfigValue == '1');
  feedbackEmail = computed(() => this.portalSettings().FeedbackEmail.ConfigValue);
  helpLink = computed(() => this.portalSettings().HelpLink.ConfigValue);
  ViewpointAnalyticsURL = computed(
    () => `${this.viewpointOneUrl}/vo/analytics/${this.portalSettings().ViewpointOneEnterpriseID.ConfigValue}/home`
  );
  showPaperless = computed(
    () =>
      this.portalSettings().ShowC1095Paperless.ConfigValue == '1' ||
      this.portalSettings().ShowW2Paperless.ConfigValue == '1' ||
      this.portalSettings().ShowPaystubPaperless.ConfigValue == '1' ||
      this.portalSettings().AllowPaperlessInfoChange.ConfigValue == '1' ||
      this.portalSettings().ShowPaperlessCarrier.ConfigValue == '1'
  );
  private enabledLineTypesInClockIn = computed(() => this.portalSettings().EnabledLineTypesInClockIn.ConfigValue);
  private payLink = computed(() => this.portalSettings().TrimblePayLink.ConfigValue);

  // other component properties
  formData: { [key: string]: any } = {};
  modules: PortalModule[];
  menu: any[] = [];
  position: GeolocationPosition;
  clockStatus: { [key: string]: any } = {};
  hotlistMenu: boolean;
  userNameMenu: boolean;
  teamMenu: boolean;
  unapprovedRequestCount: number;
  flagsExist = false;
  moduleCategories: any[];
  hotlistItems: PortalModule[] = [];
  moduleCategoriesMobile: any[];
  requestingLocation = true;
  geolocationEnabled: boolean;
  performingClockAction: boolean;
  fieldToolsCount: number = null;
  private errorMsg: string;
  hideBar: boolean = this.window.location.pathname.includes('/field/printhuddle');
  private showOfficeRoute: boolean;
  private navBarNavigationEnd$: Subscription = null;
  approvalCounts: { [key: string]: number } = {};
  private readonly approvalCountRefreshInterval: number = 60 * 1000;
  private subscriptions: Subscription[] = [];
  private licensedUser: any = {};
  private approvalCountsSub: Subscription = null;
  showMealBreak: boolean = false;
  hadMealBreakToday: boolean = false;
  mealBreakSetting: { [key: string]: string } = {};
  readonly mealBreakOptions: SimpleLookupResult[] = [
    { ComboType: null, DatabaseValue: 'N', DisplayValue: 'No' },
    { ComboType: null, DatabaseValue: 'Y', DisplayValue: 'Yes' },
  ];
  clockMoment: moment.Moment;
  lastClockDisplay: string;

  constructor() {
    effect(() => {
      this.loginMode(); //When loginmode is updated, check for password expiration
      this.checkPasswordExp();
    });
  }

  ngOnInit(): void {
    this.navBarNavigationEnd$ = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.showQuickAdd.set(this.employeeInfo.IsAuthExpense && this.window.location.pathname.indexOf(this.expenseReport) === -1);
        this.closeGlobalNav();
      }
    });

    this.subscriptions.push(
      this.eventService.onSendApprovalCountReceived((approval: string, count: number) => this.updateApprovalCount(approval, count))
    );
    this.initApprovalCounts();
    this.getMealBreakSetting();

    if (this.navBarClockInEnabled()) {
      setTimeout(() => {
        this.getClockInStatus();
      }, 1000);
    }

    this.formData.fileDrop = null;
    this.getModules();
    this.subscriptions.push(this.eventService.on('modulesChanged', () => this.getModules(true)));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub?.unsubscribe());
    if (this.navBarNavigationEnd$) {
      this.navBarNavigationEnd$.unsubscribe();
    }
    if (this.approvalCountsSub) {
      this.approvalCountsSub.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    if (this.employeeInfo.ShowConsentForm && this.showPaperless()) {
      this.openModal();
      this.paperLessModal();
    }

    // this needs to be scheduled to run every 5 minutes or so, only if the user has approval modules
    if (this.portalModules.some(m => m.ApprovalType)) {
      this.approvalCountsSub = timer(0, this.approvalCountRefreshInterval).subscribe(() => this.homeService.getApprovalCounts());
    }
  }

  mealBreakEvent(event) {
    event.stopPropagation();
  }

  onKioskClicked(event: Event, href: string): void {
    this.utilityService.isLoggedOut = true;
    this.window.location.href = href;
  }

  private initApprovalCounts(): void {
    this.portalModules.forEach(module => {
      if (module.ApprovalType) {
        this.approvalCounts[module.ApprovalType] = 0;
      }
    });
  }

  // callback method when we receive a SignalR event to update an approval count
  private updateApprovalCount(approval: string, count: number): void {
    const hasApproval = Object.prototype.hasOwnProperty.call(this.approvalCounts, approval);
    if (hasApproval) {
      if (approval === 'IssueTracker') {
        this.fieldToolsCount = count;
      } else {
        this.approvalCounts[approval] = count;
      }

      this.calculateUnapprovedRequestCount();
    }
  }

  private calculateUnapprovedRequestCount(): void {
    this.unapprovedRequestCount = Object.values(this.approvalCounts)
      .map(Number)
      .filter(x => x > 0)
      .reduce((acc, val) => acc + val, 0);

    this.flagsExist = Object.values(this.approvalCounts).includes(-1);
  }

  private getMealBreakSetting() {
    if (this.portalSettings().ClockInMealBreakMinutes.ConfigValue != null) {
      this.portalSettings().ClockInMealBreakMinutes.ConfigValue.split(';').forEach((val: string) => {
        if (val) {
          const settingParts = val.split(',');
          this.mealBreakSetting[settingParts[0] + ',' + settingParts[1]] = settingParts[2];
        }
      });

      this.showMealBreak = this.mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup] != null && !this.hadMealBreakToday;
    }
  }

  checkPasswordExp(): void {
    if (
      this.employeeInfo.LoginType != 'Identity' &&
      this.employeeInfo.PasswordResetStatus != null &&
      this.window.location.pathname.toLowerCase().indexOf('changeaccountpassword') == -1
    ) {
      const passwordResetSchedule = parseInt(this.portalSettings().PasswordResetSchedule.ConfigValue);
      if (
        (passwordResetSchedule > 0 || this.portalSettings().RequirePasswordChangeOnAdminUpdate.ConfigValue == '1') &&
        this.employeeInfo.PasswordResetStatus <= 0 &&
        this.loginMode() == 'viewpoint'
      ) {
        this.openPasswordWarningModal();
      }
    }
  }

  async getModules(refresh = false): Promise<void> {
    if (this.employeeInfo != null) {
      this.calculateUnapprovedRequestCount();
    }

    //Get Counts
    let response: PortalModule[];
    if (refresh) {
      response = await this.homeService.getModules();
    } else {
      response = this.portalModules;
    }
    if (response.find(m => m.ModuleID == 130)) {
      this.showOfficeRoute = true;
    }

    this.modules = response;
    this.groupModules();
  }

  openPayLink(): void {
    try {
      const url = new URL(this.payLink());
      this.window.open(url, '_blank');
    } catch {
      /* empty */
    }
  }

  companyLogoError(): void {
    this.logoUrlBroken.set(true);
  }

  openGlobalNav(): void {
    this.document.getElementById('globalSideNav').style.width = '100%';
    this.menu = [];
    this.hotlistMenu = false;
    this.userNameMenu = false;
  }

  groupModules(): void {
    this.moduleCategories = [];
    this.moduleCategoriesMobile = [];
    this.hotlistItems = [];

    let hotlistItems: PortalModule[] = [];
    const moduleCategories = [];
    const moduleCategoriesMobile = [];

    const temp = {};
    let temp2 = {};
    let tempMobile = [];
    const temp2Mobile = [];

    this.modules.forEach(val => {
      if (val.ModuleCatgSeq != 100) {
        const moduleExists = Object.prototype.hasOwnProperty.call(temp, val.ModuleCatgName);
        if (moduleExists && val.ModuleCatgName != null && val.ModuleShowInNav) {
          temp[val.ModuleCatgName].push(val);
        } else if (val.ModuleCatgName != null && val.ModuleShowInNav) {
          temp[val.ModuleCatgName] = [];
          temp[val.ModuleCatgName].push(val);
        }
      }
    });

    hotlistItems = this.modules.filter(m => m.IsHotlistItem && m.ModuleShowInNav);
    hotlistItems = new OrderByPipe().transform(hotlistItems, 'ModulePrettyShortName', 'asc');
    for (const key in temp) {
      let groupCount = 0;
      let managementCount = 0;
      const tempDetail = temp[key];
      tempDetail.forEach(val => {
        if (val.ModuleCatgID == 1) {
          if (temp2['My Tools'] == null) temp2['My Tools'] = {};
          if (temp2['Management'] == null) temp2['Management'] = {};
          if (val.ModuleSubCatgID == 1 || val.ModuleSubCatgID == 18 || val.ModuleSubCatgID == 33) {
            if (Object.prototype.hasOwnProperty.call(temp2['My Tools'], val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName)) {
              temp2['My Tools'][val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName].push(val);
            } else {
              temp2['My Tools'][val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName] = [];
              temp2['My Tools'][val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName].push(val);
            }
          } else {
            managementCount++;
            if (Object.prototype.hasOwnProperty.call(temp2['Management'], val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName)) {
              temp2['Management'][val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName].push(val);
            } else {
              temp2['Management'][val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName] = [];
              temp2['Management'][val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName].push(val);
              groupCount++;
            }
          }
          if (managementCount == 0) {
            delete temp2['Management'];
          }
        } else {
          if (Object.prototype.hasOwnProperty.call(temp2, val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName)) {
            temp2[val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName].push(val);
          } else {
            temp2[val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName] = [];
            temp2[val.ModuleSubCatgSeq + ',' + val.ModuleSubCatgName].push(val);
            groupCount++;
          }
        }
      });
      const temp3 = [];
      for (const key3 in temp2) {
        temp3.push({ catName: key3, items: temp2[key3], catID: tempDetail[0].ModuleCatgID });
      }
      moduleCategories.push({ barName: key, list: temp3, catID: tempDetail[0].ModuleCatgID, groupCount: groupCount });
      temp2 = {};
    }

    // insert Office Tools category before Approval if present, otherwise at the end
    if (this.showOfficeRoute) {
      //check if Approval is on the navbar
      let insertIndex = moduleCategories.indexOf(moduleCategories.find(c => c.barName == 'Approval'));
      //if Approval is not on the navbar, check for Admin
      if (insertIndex == -1) {
        insertIndex = moduleCategories.indexOf(moduleCategories.find(c => c.barName == 'Admin'));
      }
      //place Office tools to the left of approval, or admin (if no approval), else place after everything else
      insertIndex = insertIndex > -1 ? insertIndex : moduleCategories.length;
      moduleCategories.splice(insertIndex, 0, { barName: 'Office Tools', list: [], catID: 99, groupCount: 0 });
    }

    //group modules by category
    this.modules.forEach(module => {
      if (module.ModuleCatgSeq != 100 && module.ModuleShowInNav) {
        if (tempMobile[module.ModuleCatgSeq] == null) {
          tempMobile[module.ModuleCatgSeq] = [];
        }
        tempMobile[module.ModuleCatgSeq].push(module);
      }
    });

    //remove unused array values
    tempMobile = tempMobile.filter(function (n) {
      return n != undefined;
    });
    for (const catgKey in tempMobile) {
      moduleCategoriesMobile[catgKey] = {};
      temp2Mobile[catgKey] = {};
      const category = tempMobile[catgKey];
      category.forEach(module => {
        if (moduleCategoriesMobile[catgKey].CategoryName == null) {
          moduleCategoriesMobile[catgKey].CategoryName = module.ModuleCatgName;
          moduleCategoriesMobile[catgKey].ModuleCount = 0;
        }
        const subCategory = module.ModuleSubCatgName != null ? module.ModuleSubCatgName : 0;
        if (temp2Mobile[catgKey][subCategory] == null) {
          temp2Mobile[catgKey][subCategory] = [];
        }
        temp2Mobile[catgKey][subCategory].push(module);
        moduleCategoriesMobile[catgKey].ModuleCount++;
      });
    }

    for (const catgKey in temp2Mobile) {
      moduleCategoriesMobile[catgKey].Modules = [];
      const category = temp2Mobile[catgKey];
      for (const subCatgKey in category) {
        const subCategory = category[subCatgKey];
        if (subCatgKey != 'CategoryName' && subCatgKey != 'ModuleCount') {
          subCategory.sort(function (a, b) {
            if (a.ModuleSortSeq < b.ModuleSortSeq) return -1;
            if (a.ModuleSortSeq > b.ModuleSortSeq) return 1;
            return 0;
          });
          subCategory.forEach(val => {
            moduleCategoriesMobile[catgKey].Modules.push(val);
          });
        }
      }
    }

    this.hotlistItems = hotlistItems;
    this.moduleCategories = moduleCategories;
    this.moduleCategoriesMobile = moduleCategoriesMobile;

    // insert Office Tools category before Approval if present, otherwise at the end
    if (this.showOfficeRoute) {
      //check if Approval is on the navbar
      let insertIndex = this.moduleCategoriesMobile.indexOf(this.moduleCategoriesMobile.find(c => c.CategoryName == 'Approval'));
      //if Approval is not on the navbar, check for Admin
      if (insertIndex == -1) {
        insertIndex = this.moduleCategoriesMobile.indexOf(this.moduleCategoriesMobile.find(c => c.CategoryName == 'Admin'));
      }
      //place Office tools to the left of approval, or admin (if no approval), else place after everything else
      insertIndex = insertIndex > -1 ? insertIndex : this.moduleCategoriesMobile.length;
      this.moduleCategoriesMobile.splice(insertIndex, 0, { CategoryName: 'Office Tools', ModuleCount: 0, Modules: [] });
    }
  }

  openPasswordWarningModal(): void {
    this.ngbModalService.open(PasswordWarningModalComponent, { size: 'md', backdrop: 'static', keyboard: false });
  }

  paperLessModal(): void {
    const modalRef = this.ngbModalService.open(PaperlessModalComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.data = this.employeeInfo;
    modalRef.componentInstance.portalSettings = this.portalSettings();

    modalRef.result.then(() => {
      this.homeService.getEmployeeInfo().then(response => {
        this.employeeInfo = response;
      });
      this.closeModal();
    });
  }

  async postClockAction($event: any, ignoreLocation: boolean): Promise<void> {
    $event.preventDefault();
    $event.stopPropagation();
    const requestObj: ClockAction_Request = {
      GPSLat: null,
      GPSLong: null,
      JCJMKeyID: null,
      JCJPKeyID: null,
      SMTripID: null,
      SMWorkOrderScopeID: null,
      SMPayType: null,
      EMWorkOrderKeyID: null,
      EMWorkOrderItemKeyID: null,
      EquipmentKeyID: null,
      EMCostCodeKeyID: null,
      ClockTimeString: null,
      SignatureData: null,
      FromGroupClock: false,
      KioskPass: null,
      ClockAction: null,
    };
    this.performingClockAction = true;
    this.licensedUser = {};
    let lineTypes = [];
    if (this.enabledLineTypesInClockIn() != null) {
      const enabledLineTypesInClockInList = this.enabledLineTypesInClockIn().split(',');
      if (enabledLineTypesInClockInList.includes('S')) {
        this.licensedUser = await this.homeService.getLicenseCounts(this.employeeInfo.PRCo, this.employeeInfo.Employee);
      }
      lineTypes = this.clockinoutService.getEnabledLineTypes(this.licensedUser, this.employeeInfo.IsActiveTechnician);
    }

    if (!this.clockStatus.ClockAction && lineTypes.length > 0) {
      const initialState = {
        portalSettings: this.portalSettings(),
        employeeInfo: this.employeeInfo,
        lastClockinType: this.clockStatus.LastUsedClockinType ?? 'J',
        lineTypes: lineTypes,
        backdrop: 'static',
      };

      const modalRef = this.modalService.show(ClockinJobModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });

      modalRef.onHide.subscribe(() => {
        const result = modalRef.content;
        if (result.userAction == 'cancel') {
          this.performingClockAction = false;
        } else {
          if (result != null && result.userAction == 'Clock In') {
            if (!ignoreLocation && this.position?.coords) {
              requestObj.GPSLat = this.position.coords.latitude;
              requestObj.GPSLong = this.position.coords.longitude;
            }
            if (result.formData.lineType == 'J') {
              if (result.formData.selectedJob != null) {
                requestObj.JCJMKeyID = result.formData.selectedJob.JCJMKeyID;
              }
              if (result.formData.selectedPhase != null) {
                requestObj.JCJPKeyID = result.formData.selectedPhase.JCJPKeyID;
              }
            } else if (result.formData.lineType == 'S') {
              requestObj.SMTripID = result.formData.workOrder?.SMTripID;
              requestObj.SMWorkOrderScopeID = result.formData.scope?.SMWorkOrderScopeID;
              requestObj.SMPayType = result.formData.payType?.DatabaseValue;
            } else if (result.formData.lineType == 'M') {
              requestObj.EMWorkOrderKeyID = result.formData.selectedEMWorkOrder?.KeyID;
              requestObj.EMWorkOrderItemKeyID = result.formData.selectedEMWorkOrderItem?.KeyID;
              requestObj.EquipmentKeyID = result.formData.selectedEquipment?.KeyID;
              requestObj.EMCostCodeKeyID = result.formData.selectedEMCostCode?.KeyID;
            }
            const currentTime = moment();
            requestObj.ClockTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
            requestObj.MealBreak = this.formData.mealBreak == 'Y';
            this.homeService.postClockAction(requestObj).then((response: any) => {
              if (response.success) {
                this.getClockInStatus();
              } else {
                this.performingClockAction = false;
                const initialState = {
                  modalData: { modalTitle: 'An Error Ocurred', modalMessage: response.error },
                  backdrop: 'static',
                };
                this.modalService.show(ErrorModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });
              }
            });
          } else {
            this.performingClockAction = false;
          }
        }
      });
    } else {
      if (this.portalSettings().RequireSignClockOut.ConfigValue == '1' && this.clockStatus.ClockAction) {
        const initialState = {
          portalSettings: this.portalSettings(),
          employeeInfo: this.employeeInfo,
          backdrop: 'static',
        };

        const modalRef = this.modalService.show(ClockoutModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });

        modalRef.onHide.subscribe(() => {
          const result = modalRef.content.userAction;
          if (result == 'close') {
            this.performingClockAction = false;
          } else {
            if (!ignoreLocation && this.position?.coords) {
              requestObj.GPSLat = this.position.coords.latitude;
              requestObj.GPSLong = this.position.coords.longitude;
            }
            requestObj.SignatureData = result;
             const currentTime = moment();
            requestObj.MealBreak = this.formData.mealBreak == 'Y';
            requestObj.ClockTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
            this.homeService.postClockAction(requestObj).then((response: any) => {
              if (response.success) {
                this.getClockInStatus();
              } else {
                this.performingClockAction = false;
                const initialState = {
                  modalData: { modalTitle: 'An Error Ocurred', modalMessage: response.error },
                  backdrop: 'static',
                };
                this.modalService.show(ErrorModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });
              }
            });
          }
        });
      } else {
        if (!ignoreLocation && this.position?.coords) {
          requestObj.GPSLat = this.position.coords.latitude;
          requestObj.GPSLong = this.position.coords.longitude;
        }
          const currentTime = moment();
          requestObj.MealBreak = this.formData.mealBreak == 'Y';
         requestObj.ClockTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
        this.homeService.postClockAction(requestObj).then((response: any) => {
          if (response.success) {
            this.getClockInStatus();
          } else {
            this.performingClockAction = false;
            const initialState = {
              modalData: { modalTitle: 'An Error Ocurred', modalMessage: response.error },
              backdrop: 'static',
            };
            this.modalService.show(ErrorModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });
          }
        });
      }
    }
  }

  unbindViewpointID(): void {
    const initialState = {
      portalSettings: this.portalSettings(),
      employeeInfo: this.employeeInfo,
      modalData: {
        modalTitle: 'Unlink Viewpoint ID',
        modalMessage: 'Are you sure you want to unbind your Keystyle account from your Viewpoint account?',
        modalAction: 'Unbind',
        closeButtonText: 'Cancel',
        warningLevel: 'primary',
      },
      backdrop: 'static',
    };

    const modalRef = this.modalService.show(WarningModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });

    modalRef.onHide.subscribe(() => {
      const result = modalRef.content.userAction;
      if (result == 'Unbind') {
        this.homeService.unbindViewpointID().then((response: any) => {
          if (response.success) {
            const initialState = {
              portalSettings: this.portalSettings(),
              employeeInfo: this.employeeInfo,
              modalData: {
                modalTitle: 'Unlink Viewpoint ID',
                modalMessage: 'Keystyle account unbound from Viewpoint ID successfully.',
                closeButtonText: 'Close',
                alertLevel: 'success',
              },
              backdrop: 'static',
            };
            this.modalService.show(AlertModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });
          } else {
            const initialState = {
              portalSettings: this.portalSettings(),
              employeeInfo: this.employeeInfo,
              modalData: {
                modalTitle: 'Unlink Viewpoint ID',
                modalMessage: response.error,
                closeButtonText: 'Close',
                alertLevel: 'danger',
              },
              backdrop: 'static',
            };
            this.modalService.show(AlertModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });
          }
        });
      }
    });
  }

  getClockInStatus(): void {
    const currentTime = moment();
    const currentTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
    //reset meal break dropdown
    this.formData.mealBreak = 'N';
    this.homeService
      .getClockInStatus(false, currentTimeString)
      .then(response => {
        if (response != null) {
          this.performingClockAction = false;
          this.clockStatus = response;
          this.clockMoment = moment.utc(this.clockStatus.ClockTime);
          this.lastClockDisplay = this.clockMoment.format('hh:mm A');
          this.getLocation();
          if (this.clockStatus.ClockTime != null) {
            this.clockStatus.ClockTimeDisplay = moment(parseInt(this.clockStatus.ClockTime.substring(6)))
              .utc()
              .format(this.utilityService.getDateFormat().toUpperCase() + ' hh:mm a');
          }
        }
      })
      .catch(error => {
        this.performingClockAction = false;
        this.errorMsg = error?.error?.error ?? 'An error has occured';
      });
  }

  private checkCanMealBreakClockIn() {
    if (this.mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup] != null) {
      const mealBreakThreshold = parseInt(this.mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup]);
      //convert utc clock in time to local time
      const lastClockOut = moment(this.clockMoment.format('YYYY-MM-DD HH:mm:ss'));
      const minutesElapsed = moment().diff(lastClockOut, 'minutes');
      if (minutesElapsed < mealBreakThreshold) return false;
    }
    return true;
  }

  getLocation(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        this.requestingLocation = false;
        this.geolocationEnabled = true;
        this.position = position;
      });
    } else {
      this.requestingLocation = false;
    }
  }

  subStringFromComma(text: string): string {
    const temp = text.split(',');
    return temp[1];
  }

  closeMenusSection(number: any, type: any): void {
    if (number != null && this.menu[number]) {
      this.menu[number] = false;
      return;
    } else if (type === 'h' && !number && this.hotlistMenu) {
      this.hotlistMenu = false;
      return;
    } else if (type === 't' && !number && this.teamMenu) {
      this.teamMenu = false;
      return;
    } else if (type === 'n' && !number && this.userNameMenu) {
      this.userNameMenu = false;
      return;
    } else {
      for (let i = 0; i < this.moduleCategories?.length; i++) {
        this.menu[i] = false;
      }
      this.hotlistMenu = false;
      this.userNameMenu = false;
      this.teamMenu = false;

      if (number != null) {
        this.menu[number] = true;
      }
      if (type === 'h' && !number) {
        this.hotlistMenu = true;
      }
      if (type === 't' && !number) {
        this.teamMenu = true;
      }
      if (type === 'n' && !number) {
        this.userNameMenu = true;
      }
    }
  }

  logout(): void {
    sessionStorage.clear();
    this.utilityService.isLoggedOut = true;
    this.window.location.href = '/Account/Logout';
  }

  openModal(): void {
    this.document.getElementById('fadeNav').style.display = 'block';
  }

  closeModal(): void {
    this.document.getElementById('fadeNav').style.display = 'none';
  }

  closeGlobalNav(): void {
    this.document.getElementById('globalSideNav').style.width = '0';
    this.closeMenusSection(null, null);
  }
}
